import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  signIn,
  signUp,
  confirmSignUp,
  resendSignUpCode,
} from 'aws-amplify/auth';
import { useAuth } from '@/auth/AuthContext';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';

type Mode = 'signin' | 'signup' | 'confirm';

export default function Login() {
  const { user, refreshSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();

  const inviteToken = params.get('invite') ?? '';
  const initialMode = params.get('mode') === 'signup' ? 'signup' : 'signin';

  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const from = (location.state as { from?: Location })?.from?.pathname ?? '/';

  useEffect(() => {
    if (user) navigate(from, { replace: true });
  }, [user, from, navigate]);

  async function handleSignIn() {
    setLoading(true);
    setError('');
    try {
      await signIn({ username: email, password });
      await refreshSession();
      navigate(from, { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleSignUp() {
    if (!inviteToken) {
      setError('An invite link is required to create an account. Ask Richard for an invite.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            'custom:inviteToken': inviteToken,
          },
        },
      });
      setMode('confirm');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Sign up failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirm() {
    setLoading(true);
    setError('');
    try {
      await confirmSignUp({ username: email, confirmationCode: code });
      await signIn({ username: email, password });
      await refreshSession();
      navigate(from, { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Confirmation failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <h1 className="login-logo">MySommly</h1>
        <p className="login-tagline">Your personal AI wine companion</p>

        {mode === 'signin' && (
          <>
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
            {error && <p className="form-error">{error}</p>}
            <Button variant="primary" onClick={handleSignIn} loading={loading} fullWidth>
              Sign in
            </Button>
          </>
        )}

        {mode === 'signup' && (
          <>
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              hint="At least 8 characters with a number"
            />
            {error && <p className="form-error">{error}</p>}
            <Button variant="primary" onClick={handleSignUp} loading={loading} fullWidth>
              Create account
            </Button>
            <Button variant="ghost" onClick={() => setMode('signin')} fullWidth>
              Already have an account
            </Button>
          </>
        )}

        {mode === 'confirm' && (
          <>
            <p className="login-instruction">
              Check your email for a verification code and enter it below.
            </p>
            <Input
              label="Verification code"
              type="text"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoComplete="one-time-code"
            />
            {error && <p className="form-error">{error}</p>}
            <Button variant="primary" onClick={handleConfirm} loading={loading} fullWidth>
              Verify
            </Button>
            <Button
              variant="ghost"
              onClick={() => resendSignUpCode({ username: email })}
              fullWidth
            >
              Resend code
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
